/**
 * TransferFiltersStep - Step 4: Filtros opcionais para refinar a transferência
 * 
 * RELEASE: 2.1.0 - 2025-01-22
 * 
 * Permite aplicar filtros como:
 * - Status (apenas para conversas)
 * - Período (data de criação)
 * - Departamento (para conversas)
 * - Workspace → Pipeline → Fases (hierarquia para negócios)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { TransferType, TransferFilters, TRANSFER_TYPE_LABELS, MAX_BULK_TRANSFER_ITEMS } from '@/hooks/useBulkTransfer';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Filter, Calendar, Building2, Layers, Briefcase, CheckCircle2, MessageSquare, LayoutGrid, Hash } from 'lucide-react';

// =============================================================================
// TIPOS
// =============================================================================

interface TransferFiltersStepProps {
  transferType: TransferType;
  filters: TransferFilters;
  onFiltersChange: (filters: Partial<TransferFilters>) => void;
}

interface Workspace {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
}

interface Pipeline {
  id: string;
  name: string;
  workspace_id: string;
}

interface Phase {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
}

// =============================================================================
// CONSTANTES
// =============================================================================

// Constante para representar "todos" (evita string vazia no SelectItem)
const ALL_VALUE = '__all__';

// Status apenas para conversas
const CONVERSATION_STATUSES = [
  { value: ALL_VALUE, label: 'Todos os status' },
  { value: 'pending', label: 'Pendente' },
  { value: 'open', label: 'Aberto' },
];

// v2.4.0: Opções de fase kanban (calculada dinamicamente no SQL)
const QUEUE_SITUATION_OPTIONS = [
  { value: 'waiting_bot', label: 'No Bot', color: '#6b7280' },
  { value: 'waiting_agent', label: 'Aguardando Agente', color: '#f59e0b' },
  { value: 'pending_transfer', label: 'Transferências', color: '#8b5cf6' },
  { value: 'in_service', label: 'Em Atendimento', color: '#22c55e' },
];

// =============================================================================
// COMPONENTE
// =============================================================================

export function TransferFiltersStep({
  transferType,
  filters,
  onFiltersChange,
}: TransferFiltersStepProps) {
  const { currentTenant } = useAuth();

  // Determinar quais filtros mostrar baseado no tipo de transferência
  const isConversation = transferType === 'conversations';
  const isDepartmentTransfer = transferType === 'conversations_department'; // v2.5.0
  const isDeal = transferType === 'deals_assigned' || transferType === 'deals_seller';
  const isContact = transferType === 'contacts_seller';
  
  // v2.5.1: Departamento (Conversa) também deve exibir filtros de inbox e fase kanban
  const showDepartmentFilter = isConversation;
  const showWorkspaceFilter = isDeal;
  const showStatusFilter = isConversation || isDepartmentTransfer;
  const showInboxFilter = isConversation || isDepartmentTransfer;
  const showQueueSituationFilter = isConversation || isDepartmentTransfer;

  // =========================================================================
  // QUERY: DEPARTAMENTOS (para conversas)
  // =========================================================================

  const { data: departments } = useQuery({
    queryKey: ['transfer-departments', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: showDepartmentFilter && !!currentTenant?.id,
  });

  // =========================================================================
  // QUERY: INBOXES ATIVAS (v2.4.0 - para conversas)
  // =========================================================================

  const { data: inboxes } = useQuery({
    queryKey: ['transfer-inboxes', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, identifier, channel_type')
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: (isConversation || isDepartmentTransfer) && !!currentTenant?.id,
  });

  // =========================================================================
  // QUERY: WORKSPACES (para negócios)
  // =========================================================================

  const { data: workspaces } = useQuery<Workspace[]>({
    queryKey: ['transfer-workspaces', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('id, name, icon, color')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
    enabled: showWorkspaceFilter && !!currentTenant?.id,
  });

  // =========================================================================
  // QUERY: PIPELINES (filtrado por workspace se selecionado)
  // =========================================================================

  const { data: pipelines } = useQuery<Pipeline[]>({
    queryKey: ['transfer-pipelines', currentTenant?.id, filters.workspace_id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      let query = supabase
        .from('crm_pipelines')
        .select('id, name, workspace_id')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true);

      // Filtrar por workspace se selecionado
      if (filters.workspace_id) {
        query = query.eq('workspace_id', filters.workspace_id);
      }

      const { data, error } = await query.order('display_order');

      if (error) throw error;
      return data || [];
    },
    enabled: showWorkspaceFilter && !!currentTenant?.id,
  });

  // =========================================================================
  // QUERY: FASES (filtrado por pipeline selecionado)
  // =========================================================================

  const { data: phases } = useQuery({
    queryKey: ['transfer-phases', filters.pipeline_id],
    queryFn: async (): Promise<Phase[]> => {
      if (!filters.pipeline_id) return [];

      // Usar any para evitar TS2589 causado pela profundidade de tipos do Supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (supabase as any)
        .from('crm_pipeline_phases')
        .select('id, name, color, sort_order')
        .eq('pipeline_id', filters.pipeline_id)
        .eq('is_active', true)
        .order('sort_order');

      if (result.error) throw result.error;
      return (result.data ?? []) as Phase[];
    },
    enabled: !!filters.pipeline_id,
  });

  // =========================================================================
  // HANDLERS
  // =========================================================================

  // Handler para status (apenas conversas)
  const handleStatusChange = (value: string) => {
    onFiltersChange({ status: value === ALL_VALUE ? undefined : value });
  };

  // Handler para departamento (apenas conversas)
  const handleDepartmentChange = (value: string) => {
    onFiltersChange({ department_id: value === ALL_VALUE ? undefined : value });
  };

  // Handler para workspace - limpa pipeline e fases ao mudar
  const handleWorkspaceChange = (value: string) => {
    onFiltersChange({ 
      workspace_id: value === ALL_VALUE ? undefined : value,
      pipeline_id: undefined,
      phase_ids: undefined,
    });
  };

  // Handler para pipeline - limpa fases ao mudar
  const handlePipelineChange = (value: string) => {
    onFiltersChange({ 
      pipeline_id: value === ALL_VALUE ? undefined : value,
      phase_ids: undefined,
    });
  };

  // Handler para toggle de fase (seleção múltipla)
  const handlePhaseToggle = (phaseId: string) => {
    const current = filters.phase_ids || [];
    const updated = current.includes(phaseId)
      ? current.filter(id => id !== phaseId)
      : [...current, phaseId];
    onFiltersChange({ phase_ids: updated.length > 0 ? updated : undefined });
  };

  // v2.4.0: Handler para toggle de inbox (seleção múltipla)
  const handleInboxToggle = (inboxId: string) => {
    const current = filters.inbox_ids || [];
    const updated = current.includes(inboxId)
      ? current.filter(id => id !== inboxId)
      : [...current, inboxId];
    onFiltersChange({ inbox_ids: updated.length > 0 ? updated : undefined });
  };

  // v2.4.0: Handler para toggle de fase kanban (seleção múltipla)
  const handleSituationToggle = (situation: string) => {
    const current = filters.queue_situations || [];
    const updated = current.includes(situation)
      ? current.filter(s => s !== situation)
      : [...current, situation];
    onFiltersChange({ queue_situations: updated.length > 0 ? updated : undefined });
  };

  // Handler para datas
  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ date_from: e.target.value || undefined });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ date_to: e.target.value || undefined });
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">Filtros opcionais</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Refine a seleção de {TRANSFER_TYPE_LABELS[transferType].toLowerCase()} a transferir
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtros de Seleção
          </CardTitle>
          <CardDescription>
            Deixe em branco para transferir todos os registros disponíveis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ============================================================= */}
          {/* v2.5.0: CAMPO DE LIMITE (para TODOS os tipos)                */}
          {/* ============================================================= */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Hash className="h-4 w-4" />
              Quantidade máxima de registros
            </Label>
            <Input
              type="number"
              min={1}
              max={MAX_BULK_TRANSFER_ITEMS}
              value={filters.transfer_limit ?? MAX_BULK_TRANSFER_ITEMS}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) {
                  onFiltersChange({ 
                    transfer_limit: Math.min(Math.max(val, 1), MAX_BULK_TRANSFER_ITEMS) 
                  });
                }
              }}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Limite de {MAX_BULK_TRANSFER_ITEMS} registros por execução para garantir performance.
              Para transferir mais, repita o procedimento.
            </p>
          </div>

          {/* ============================================================= */}
          {/* FILTROS PARA CONVERSAS                                        */}
          {/* ============================================================= */}
          
          {/* Filtro de Status (apenas conversas) */}
          {showStatusFilter && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Status
              </Label>
              <Select
                value={filters.status || ALL_VALUE}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  {CONVERSATION_STATUSES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filtro de Departamento (conversas) */}
          {showDepartmentFilter && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Departamento
              </Label>
              <Select
                value={filters.department_id || ALL_VALUE}
                onValueChange={handleDepartmentChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os departamentos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>Todos os departamentos</SelectItem>
                  {departments?.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ============================================================= */}
          {/* v2.4.0: FILTRO DE INBOX (apenas conversas - seleção múltipla) */}
          {/* ============================================================= */}
          {showInboxFilter && inboxes && inboxes.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Inbox (múltipla seleção)
              </Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/30">
                {inboxes.map((inbox) => {
                  const isSelected = filters.inbox_ids?.includes(inbox.id);
                  return (
                    <Badge
                      key={inbox.id}
                      variant={isSelected ? "default" : "outline"}
                      className="cursor-pointer transition-all hover:scale-105"
                      onClick={() => handleInboxToggle(inbox.id)}
                    >
                      {inbox.name}
                    </Badge>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Clique nas inboxes para selecionar. Se nenhuma for selecionada, todas serão incluídas.
              </p>
            </div>
          )}

          {/* ============================================================= */}
          {/* v2.4.0: FILTRO DE FASE KANBAN (apenas conversas - seleção múltipla) */}
          {/* ============================================================= */}
          {showQueueSituationFilter && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <LayoutGrid className="h-4 w-4" />
                Fase da Fila (múltipla seleção)
              </Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/30">
                {QUEUE_SITUATION_OPTIONS.map((option) => {
                  const isSelected = filters.queue_situations?.includes(option.value);
                  return (
                    <Badge
                      key={option.value}
                      variant={isSelected ? "default" : "outline"}
                      className="cursor-pointer transition-all hover:scale-105"
                      style={{
                        backgroundColor: isSelected ? option.color : undefined,
                        borderColor: option.color,
                      }}
                      onClick={() => handleSituationToggle(option.value)}
                    >
                      {option.label}
                    </Badge>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Filtre por situação na fila. Se nenhuma for selecionada, todas serão incluídas.
              </p>
            </div>
          )}

          {/* ============================================================= */}
          {/* FILTROS PARA NEGÓCIOS (Hierarquia: Workspace → Pipeline → Fases) */}
          {/* ============================================================= */}

          {/* Filtro de Workspace (negócios) */}
          {showWorkspaceFilter && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Workspace
              </Label>
              <Select
                value={filters.workspace_id || ALL_VALUE}
                onValueChange={handleWorkspaceChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os workspaces" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>Todos os workspaces</SelectItem>
                  {workspaces?.map((ws) => (
                    <SelectItem key={ws.id} value={ws.id}>
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Se não selecionar, a transferência será de todos os workspaces
              </p>
            </div>
          )}

          {/* Filtro de Pipeline (negócios - filtrado por workspace) */}
          {showWorkspaceFilter && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Pipeline
              </Label>
              <Select
                value={filters.pipeline_id || ALL_VALUE}
                onValueChange={handlePipelineChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os pipelines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>Todos os pipelines</SelectItem>
                  {pipelines?.map((pipe) => (
                    <SelectItem key={pipe.id} value={pipe.id}>
                      {pipe.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filtro de Fases (múltipla seleção - aparece após selecionar pipeline) */}
          {showWorkspaceFilter && filters.pipeline_id && phases && phases.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Fases (múltipla seleção)
              </Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/30">
                {phases.map((phase) => {
                  const isSelected = filters.phase_ids?.includes(phase.id);
                  return (
                    <Badge
                      key={phase.id}
                      variant={isSelected ? "default" : "outline"}
                      className="cursor-pointer transition-all hover:scale-105"
                      style={{
                        backgroundColor: isSelected && phase.color ? phase.color : undefined,
                        borderColor: phase.color || undefined,
                      }}
                      onClick={() => handlePhaseToggle(phase.id)}
                    >
                      {phase.name}
                    </Badge>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Clique nas fases para selecionar. Se nenhuma for selecionada, todas serão incluídas.
              </p>
            </div>
          )}

          {/* ============================================================= */}
          {/* FILTRO DE PERÍODO (comum para todos os tipos)                 */}
          {/* ============================================================= */}

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Período de Criação
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">De</Label>
                <Input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={handleDateFromChange}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Até</Label>
                <Input
                  type="date"
                  value={filters.date_to || ''}
                  onChange={handleDateToChange}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-center text-muted-foreground">
        Os filtros são opcionais. Clique em "Próximo" para ver o preview da transferência.
      </p>
    </div>
  );
}
